import React, { useRef, useEffect } from 'react';

/**
 * Rumsmått-komponent med autofokus och automatisk beräkning
 * Används i EstimatPage för rumsmått-sektion
 */
const RoomMeasurementCard = ({
  room,
  shouldFocus,
  onFocused,
  onFieldChange,
  onDelete,
  onToggleEdit,
  selected,
  onToggleSelected,
  dragHandleProps,
}) => {
  const nameRef = useRef(null);
  const floorAreaRef = useRef(null);
  
  useEffect(() => {
    if (shouldFocus) {
      if (room.isEditing && nameRef.current) {
        // Fokusera på namn om det är tomt (nytt rum utan förifyllt namn)
        nameRef.current.focus();
        onFocused && onFocused();
      } else if (!room.isEditing && room.name && floorAreaRef.current) {
        // Fokusera på golvyta om namnet redan är ifyllt (standardrum)
        floorAreaRef.current.focus();
        onFocused && onFocused();
      }
    }
  }, [shouldFocus, room.isEditing, room.name, onFocused]);

  return (
    <div className={`bg-white p-4 rounded-lg border shadow-sm transition-colors ${selected ? 'border-leadme-pink ring-1 ring-leadme-pink' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between mb-3 gap-2">
        {/* Draghandtag. Eget grepp i stället för hela kortet — annars går det
            inte att markera text i namn- och måttfälten. */}
        {dragHandleProps && (
          <span
            {...dragHandleProps}
            title="Dra för att ändra ordning"
            className="mt-0.5 text-gray-300 hover:text-leadme-pink cursor-grab active:cursor-grabbing flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M7 4a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm0 6a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm0 6a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm9-12a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm0 6a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zm0 6a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
            </svg>
          </span>
        )}
        {onToggleSelected && (
          <input
            type="checkbox"
            checked={!!selected}
            onChange={onToggleSelected}
            title="Inkludera i summering"
            className="mt-1 w-4 h-4 text-leadme-pink rounded focus:ring-leadme-pink cursor-pointer"
          />
        )}
        <div className="flex-1">
          {room.isEditing ? (
            <input
              ref={nameRef}
              type="text"
              value={room.name ?? ''}
              onChange={(e) => onFieldChange('name', e.target.value)}
              onBlur={() => {
                onToggleEdit();
                // Spara när rumsnamnet är klart
                setTimeout(() => onFieldChange('_save', true), 100);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onToggleEdit();
                  // Spara när rumsnamnet är klart
                  setTimeout(() => onFieldChange('_save', true), 100);
                }
              }}
              placeholder="Rumsnamn (t.ex. Vardagsrum, Kök...)"
              className="w-full px-2 py-1 text-sm font-semibold border border-gray-300 rounded focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink"
            />
          ) : (
            <h4 
              className="text-sm font-semibold text-gray-800 cursor-pointer hover:text-leadme-pink"
              onClick={onToggleEdit}
            >
              {room.name || 'Klicka för att namnge rum'}
            </h4>
          )}
        </div>
        <button
          onClick={onDelete}
          className="ml-2 text-red-500 hover:text-red-700 text-sm"
          title="Ta bort rum"
        >
          ×
        </button>
      </div>
      
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Golvyta (kvm)
          </label>
          <input
            ref={floorAreaRef}
            type="text"
            inputMode="decimal"
            value={room.floorArea ?? ''}
            onChange={(e) => onFieldChange('floorArea', e.target.value.replace(',', '.'))}
            onBlur={() => onFieldChange('_save', true)}
            placeholder="0"
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Takhöjd (m)
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={room.ceilingHeight ?? ''}
            onChange={(e) => onFieldChange('ceilingHeight', e.target.value.replace(',', '.'))}
            onBlur={() => onFieldChange('_save', true)}
            placeholder="0"
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink"
          />
        </div>
      </div>
      
      {/* Beräknade värden */}
      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100">
        <div className="text-center">
          <div className="text-xs text-gray-500 mb-1">Väggyta</div>
          <div className="text-sm font-semibold text-leadme-pink">
            {room.wallArea > 0 ? `${room.wallArea} m²` : '-'}
          </div>
        </div>
        <div className="text-center">
          <div className="text-xs text-gray-500 mb-1">Omkrets</div>
          <div className="text-sm font-semibold text-blue-600">
            {room.perimeter > 0 ? `${room.perimeter} m` : '-'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomMeasurementCard;
